import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge as GaugeIcon, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { OdometerCard } from "@/components/OdometerCard";
import { ComplianceTrendDialog } from "@/components/ComplianceTrendDialog";
import { api, apiError, fmtLakhValue, fmtPct } from "@/lib/api";

/**
 * The Dashboard's compliance odometers.
 *
 * A card is here because a usecase is on compliance tracking and showing — nothing is configured
 * by hand, and a usecase that is taken off tracking or hidden simply stops appearing. Newest
 * filled first and highest cumulative next, until an officer drags their own arrangement, which is
 * kept on their own account.
 */
export const OdometerBoard = ({ plants = "", fys = "", onCount }) => {
  const [data, setData] = useState(null);
  const [cards, setCards] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [held, setHeld] = useState(null);
  const from = useRef(null);

  const load = () =>
    api
      .get("/compliance/odometers", { params: { plants, fys } })
      .then((r) => {
        setData(r.data);
        setCards(r.data.cards);
        onCount?.(r.data.cards.length);
      })
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plants, fys]);

  const drop = (toIndex) => {
    const fromIndex = from.current;
    if (fromIndex === null || fromIndex === toIndex) return;
    const next = [...cards];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCards(next);
    from.current = null;
    setHeld(null);
    api
      .put("/compliance/order", { order: next.map((c) => c.project_id) })
      .then(() => setData((d) => ({ ...d, arranged_by_hand: true })))
      .catch((e) => toast.error(apiError(e)));
  };

  const reset = () =>
    api
      .put("/compliance/order", { order: [] })
      .then(() => {
        toast.success("Back to newest first.");
        load();
      })
      .catch((e) => toast.error(apiError(e)));

  if (!data)
    return (
      <div className="flex h-32 items-center justify-center border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;

  return (
    <section data-testid="compliance-odometer-container">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps flex items-center gap-2 text-primary">
            <GaugeIcon className="h-3.5 w-3.5" /> Compliance odometers
          </div>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            How the live usecases are actually performing
          </h2>
          <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
            {t.use_cases
              ? `${t.use_cases} usecase(s) under compliance watch · ${t.reading_days} day(s) on record · cumulative ${fmtPct(t.avg_cumulative_pct)} · impact ${fmtLakhValue(t.actual_lakh)} against ${fmtLakhValue(t.target_lakh)} committed.`
              : "Nothing is under compliance watch yet."}{" "}
            Press a card for its trend. Drag one by its handle to put it where you want it.
            {t.incomplete
              ? ` ${t.incomplete} of them still has a target, a baseline or a target impact to declare.`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.arranged_by_hand && (
            <button
              type="button"
              data-testid="odometer-reset-order"
              onClick={reset}
              className="flex items-center gap-1.5 border border-border px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
            >
              <RotateCcw className="h-3 w-3" /> Newest first
            </button>
          )}
          <Link
            to="/compliance-data"
            data-testid="odometer-manage"
            className="border border-primary px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest text-primary transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
          >
            Compliance data
          </Link>
        </div>
      </div>

      {!cards.length ? (
        <div className="mt-3 border border-border bg-card p-8" data-testid="odometers-empty">
          <GaugeIcon className="h-6 w-6 text-muted-foreground" />
          <h3 className="mt-3 font-display text-lg font-bold">
            No usecase is under compliance watch in this view
          </h3>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            An odometer appears here the moment a usecase is put on compliance tracking — pick one
            from the hot trial onwards on the Compliance Data screen, settle its target KPI,
            baseline and target impact, and fill the first day.
          </p>
          <Link
            to="/compliance-data"
            data-testid="odometers-empty-cta"
            className="mt-5 inline-block bg-primary px-4 py-2.5 font-display text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90"
          >
            Put a usecase on compliance watch
          </Link>
        </div>
      ) : (
        <div className="mt-3 space-y-px border border-border bg-border">
          {cards.map((card, i) => (
            <OdometerCard
              key={card.project_id}
              card={card}
              onOpen={setOpenId}
              draggable
              dragging={held === card.project_id}
              onDragStart={() => {
                from.current = i;
                setHeld(card.project_id);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                drop(i);
              }}
              onDragEnd={() => setHeld(null)}
            />
          ))}
        </div>
      )}

      <ComplianceTrendDialog projectId={openId} onClose={() => setOpenId(null)} />
    </section>
  );
};
